import { useState, useEffect } from 'react';
import { Box, Flex, IconButton, Button, Badge } from '@chakra-ui/react';
import { MdDelete, MdEdit, MdSearch, MdRefresh } from 'react-icons/md';

import Page from '../components/common/Page';
import { ApiCommand } from '../lib/Api';
import useToastNotification from '../lib/hooks/useToastNotification';
import StrongTable from '../components/common/StrongTable';
import StrongPagination from '../components/common/StrongPagination';
import StrongInput from '../components/common/StrongInput';
import useApi from '../lib/hooks/useApi';
import urlConstants from '../lib/constants/url.constants';
import EditHomeIntroModal from '../components/homeIntros/EditHomeIntroModal';

const APPEAR_RULE_LABELS: Record<string, string> = {
  first_open: 'First login only',
  every_open: 'Every open',
};

const COLUMNS = [
  { key: 'title', label: 'Title' },
  {
    key: 'appearRule',
    label: 'Appear Rule',
    render: (value: string) => APPEAR_RULE_LABELS[value] ?? value,
  },
  {
    key: 'isActive',
    label: 'Active',
    render: (value: boolean) => (
      <Badge colorScheme={value ? 'green' : 'gray'}>{value ? 'Active' : 'Inactive'}</Badge>
    ),
  },
];

const {
  getHomeIntros: getHomeIntrosUrl,
  createHomeIntro: createHomeIntroUrl,
  editHomeIntro: editHomeIntroUrl,
  deleteHomeIntro: deleteHomeIntroUrl,
  resetSeenHomeIntro: resetSeenHomeIntroUrl,
} = urlConstants.homeIntros;

const HomeIntros = () => {
  const [intros, setIntros] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedIntro, setSelectedIntro] = useState<any | undefined>();

  const showToast = useToastNotification();

  const { loading: isLoadingIntros, sendRequest: getHomeIntros } = useApi<any>();
  const { sendRequest: createHomeIntro } = useApi<any>();
  const { sendRequest: editHomeIntro } = useApi<any>();
  const { sendRequest: deleteHomeIntro } = useApi<any>();
  const { sendRequest: resetSeenHomeIntro } = useApi<any>();

  const fetchIntros = () => {
    getHomeIntros({
      callback: (data: any, error: string | null) => {
        if (error) { showToast({ title: 'Failed', description: error, status: 'error' }); return; }
        if (!data) return;
        setIntros(data.intros ?? []);
        const total = data.totalCount ?? 0;
        setTotalCount(total);
        setTotalPages(Math.ceil(total / pageSize) || 1);
      },
      command: ApiCommand.GET,
      url: getHomeIntrosUrl,
      options: { page: currentPage, limit: pageSize },
    });
  };

  useEffect(() => {
    fetchIntros();
  }, [currentPage, pageSize]);

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const onSave = (formData: FormData) => {
    if (selectedIntro) {
      formData.append('id', selectedIntro._id);
      editHomeIntro({
        callback: (_data: any, error: string | null) => {
          if (error) { showToast({ title: 'Failed', description: error, status: 'error' }); return; }
          showToast({ title: 'Success', description: 'Home intro updated', status: 'success' });
          setIsOpenModal(false);
          fetchIntros();
        },
        command: ApiCommand.PUT,
        url: editHomeIntroUrl,
        options: formData,
      });
    } else {
      createHomeIntro({
        callback: (_data: any, error: string | null) => {
          if (error) { showToast({ title: 'Failed', description: error, status: 'error' }); return; }
          showToast({ title: 'Success', description: 'Home intro created', status: 'success' });
          setIsOpenModal(false);
          fetchIntros();
        },
        command: ApiCommand.POST,
        url: createHomeIntroUrl,
        options: formData,
      });
    }
  };

  const onDelete = (id: string) => {
    deleteHomeIntro({
      callback: (_data: any, error: string | null) => {
        if (error) { showToast({ title: 'Failed', description: error, status: 'error' }); return; }
        showToast({ title: 'Success', description: 'Home intro deleted', status: 'success' });
        fetchIntros();
      },
      command: ApiCommand.DELETE,
      url: `${deleteHomeIntroUrl}?id=${id}`,
    });
  };

  const onEdit = (id: string) => {
    setSelectedIntro(intros.find((i) => i._id === id));
    setIsOpenModal(true);
  };

  const onResetSeen = (id: string) => {
    resetSeenHomeIntro({
      callback: (_data: any, error: string | null) => {
        if (error) { showToast({ title: 'Failed', description: error, status: 'error' }); return; }
        showToast({ title: 'Success', description: 'Seen state reset for all users', status: 'success' });
      },
      command: ApiCommand.POST,
      url: `${resetSeenHomeIntroUrl}/${id}/reset-seen`,
    });
  };

  return (
    <Page>
      <Flex justifyContent='space-between' alignItems='center' mb={6} gap={4} flexWrap='wrap'>
        <Box flex='1' maxW='400px'>
          <StrongInput placeholder='Search' rightIcon={<MdSearch color='gray.500' />} />
        </Box>
        <Button
          colorScheme='primary'
          px={6}
          py={4}
          borderRadius='md'
          fontWeight='bold'
          _hover={{ bg: 'primary.300' }}
          onClick={() => { setSelectedIntro(undefined); setIsOpenModal(true); }}
        >
          Create Home Intro
        </Button>
      </Flex>

      <Flex flexDirection='column' height='full' justifyContent='space-between'>
        <Box backgroundColor='white' borderRadius='lg' height='calc(100vh - 275px)'>
          <StrongTable
            columns={COLUMNS}
            data={intros}
            loading={isLoadingIntros}
            actions={(row) => (
              <>
                <IconButton
                  aria-label='Edit'
                  icon={<MdEdit />}
                  variant='outline'
                  colorScheme='primary'
                  size='sm'
                  marginRight={4}
                  onClick={() => onEdit(row._id)}
                />
                <IconButton
                  aria-label='Reset Seen'
                  icon={<MdRefresh />}
                  variant='outline'
                  colorScheme='orange'
                  size='sm'
                  marginRight={4}
                  title='Reset seen state for all users'
                  onClick={() => onResetSeen(row._id)}
                />
                <IconButton
                  aria-label='Delete'
                  icon={<MdDelete />}
                  variant='outline'
                  colorScheme='red'
                  size='sm'
                  onClick={() => onDelete(row._id)}
                />
              </>
            )}
          />
        </Box>

        <StrongPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
        />
      </Flex>

      <EditHomeIntroModal
        isOpen={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        onSubmit={onSave}
        selectedIntro={selectedIntro}
      />
    </Page>
  );
};

export default HomeIntros;
